'use client'

import { getChainConfigEntry } from '@/config/chain'
import { SUPPORTED_CHAIN_IDS } from '@/config/chain/constants'
import { Path, getSupportedPages, paths } from '@/config/paths'
import { usePathname } from 'next/navigation'
import { useChainId } from 'wagmi'

export interface UsePageChainIdResult {
  chainId: number
  pageSupported: boolean
  pageName: string
}

export function usePageChainId(): UsePageChainIdResult {
  const chainId = useChainId()
  const pathname = usePathname()
  const supportedPages = getSupportedPages(getChainConfigEntry(chainId))

  type SupportedPage = (typeof supportedPages)[number]

  // Find the most specific matching path
  const currentPage = Object.entries(paths)
    .sort(([, a], [, b]) => b.length - a.length) // Sort by length (longest first)
    .find(([_, path]) => {
      // Special handling for root path - must be exact match
      if (path === '/') {
        return pathname === '/'
      }
      return pathname?.startsWith(path)
    })?.[0]

  const pageName = pageNamesMap[currentPage as Path]
  const defaultChainId = SUPPORTED_CHAIN_IDS[0] as number

  if (!currentPage) {
    return { chainId: defaultChainId, pageSupported: true, pageName: '' }
  }

  if (currentPage === 'bridge') {
    return { chainId: defaultChainId, pageSupported: true, pageName }
  }

  if (supportedPages.includes(currentPage as SupportedPage)) {
    return { chainId: defaultChainId, pageSupported: true, pageName }
  }

  return { chainId: defaultChainId, pageSupported: false, pageName }
}

const pageNamesMap: Record<Path, string> = {
  home: 'Home',
  easyBorrow: 'Easy Borrow',
  dashboardPooled: 'Dashboard',
  dashboardIsolated: 'Dashboard',
  dashboardLendBorrow: 'Dashboard',
  dashboardEarn: 'Dashboard',
  marketsEBorrow: 'Markets',
  activity: 'Activity',
  pooledDetails: 'Pooled Markets',
  eLendDetails: 'Lend Details',
  eBorrowDetails: 'Borrow Details',
  swap: 'Swap',
  trade: 'Trade',
  bridge: 'Bridge',
  bridgeHyperCore: 'Bridge',
  isolatedDetails: 'Isolated',
  usdxl: 'USDXL',
  earn: 'Earn',
  earnAssets: 'Earn',
  earnVaults: 'Earn',
  lendDetails: 'Lend Details',
  deprecatedVaultDetails: 'Vault Details',
  privacyPolicy: 'Privacy Policy',
  termsOfService: 'Terms of Service',
  points: 'Points',
  card: 'Card',
  cardTelegramNotifications: 'Telegram Notifications',
  borrow: 'Borrow',
  opportunities: 'Opportunities',
}
